import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { campagneApi, fornitoriApi, clientiApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Save, Plus, Trash2, Radio, Calendar, Euro, TrendingUp, AlertCircle, MapPin, X, Filter } from 'lucide-react';
import { toast } from 'sonner';
import CalendarioProgrammazione from '@/components/CalendarioProgrammazione';

// Lista province italiane
const PROVINCE_ITALIANE = [
  "AG", "AL", "AN", "AO", "AP", "AQ", "AR", "AT", "AV", "BA", "BG", "BI", "BL", "BN", "BO", "BR", "BS", "BT", "BZ",
  "CA", "CB", "CE", "CH", "CL", "CN", "CO", "CR", "CS", "CT", "CZ", "EN", "FC", "FE", "FG", "FI", "FM", "FR", "GE",
  "GO", "GR", "IM", "IS", "KR", "LC", "LE", "LI", "LO", "LT", "LU", "MB", "MC", "ME", "MI", "MN", "MO", "MS", "MT",
  "NA", "NO", "NU", "OR", "PA", "PC", "PD", "PE", "PG", "PI", "PN", "PO", "PR", "PT", "PU", "PV", "PZ", "RA", "RC",
  "RE", "RG", "RI", "RM", "RN", "RO", "SA", "SI", "SO", "SP", "SR", "SS", "SU", "SV", "TA", "TE", "TN", "TO", "TP",
  "TR", "TS", "TV", "UD", "VA", "VB", "VC", "VE", "VI", "VR", "VT", "VV"
];

const CostruisciCampagnaPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campagna, setCampagna] = useState(null);
  const [fornitori, setFornitori] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingRow, setAddingRow] = useState(false);
  
  const [newRow, setNewRow] = useState({
    fornitore_id: '',
    emittente_numero: 0,
    durata_spot: '30sec',
    numero_spot: 1
  });

  const [fornitoreSearch, setFornitoreSearch] = useState('');
  const [selectedFornitore, setSelectedFornitore] = useState(null);
  const [showFornitoreDropdown, setShowFornitoreDropdown] = useState(false);
  
  // Filtro per copertura provinciale
  const [provinceFilter, setProvinceFilter] = useState([]);
  const [showProvinceSelector, setShowProvinceSelector] = useState(false);
  const [provinciaSearch, setProvinciaSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [campagnaRes, fornitoriRes, clientiRes] = await Promise.all([
        campagneApi.getOne(id),
        fornitoriApi.getAll(),
        clientiApi.getAll()
      ]);
      setCampagna(campagnaRes.data);
      setFornitori(fornitoriRes.data);
      setClienti(clientiRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
      navigate('/campagne');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id) fetchData();
  }, [id, fetchData]);

  // Filtered fornitori for autocomplete (search by code OR name AND province coverage)
  const filteredFornitori = useMemo(() => {
    let result = fornitori;
    
    // Filtra per testo (codice o denominazione)
    if (fornitoreSearch) {
      const search = fornitoreSearch.toLowerCase();
      result = result.filter(f => 
        f.denominazione_sociale.toLowerCase().includes(search) ||
        f.codice.toLowerCase().includes(search)
      );
    }
    
    // Filtra per copertura provinciale
    if (provinceFilter.length > 0) {
      result = result.filter(f => {
        // Controlla se almeno un'emittente copre almeno una delle province selezionate
        const emittenti = f.emittenti || [];
        // Supporta anche vecchio formato
        if (emittenti.length === 0) {
          const oldEmittenti = [f.emittente1, f.emittente2, f.emittente3].filter(e => e?.nome);
          return oldEmittenti.some(em => 
            provinceFilter.some(prov => (em.copertura_provinciale || []).includes(prov))
          );
        }
        return emittenti.some(em => 
          provinceFilter.some(prov => (em.copertura_provinciale || []).includes(prov))
        );
      });
    }
    
    return result;
  }, [fornitori, fornitoreSearch, provinceFilter]);

  // Funzione per ottenere le emittenti che coprono le province filtrate
  const getEmittentiMatchingFilter = (fornitore) => {
    if (provinceFilter.length === 0) return null;
    
    const emittenti = fornitore.emittenti || [];
    if (emittenti.length === 0) {
      // Vecchio formato
      return [fornitore.emittente1, fornitore.emittente2, fornitore.emittente3]
        .filter(e => e?.nome)
        .filter(em => provinceFilter.some(prov => (em.copertura_provinciale || []).includes(prov)))
        .map(em => em.nome);
    }
    
    return emittenti
      .filter(em => provinceFilter.some(prov => (em.copertura_provinciale || []).includes(prov)))
      .map(em => em.nome);
  };

  const emittentiDisponibili = useMemo(() => {
    if (!selectedFornitore) return [];
    const emittenti = [];
    
    // Supporta nuovo formato (array emittenti)
    if (selectedFornitore.emittenti && Array.isArray(selectedFornitore.emittenti)) {
      selectedFornitore.emittenti.forEach((em, index) => {
        if (em?.nome) {
          emittenti.push({ numero: index, nome: em.nome, data: em });
        }
      });
    } else {
      // Retrocompatibilità: vecchio formato (emittente1/2/3)
      if (selectedFornitore.emittente1?.nome) emittenti.push({ numero: 0, nome: selectedFornitore.emittente1.nome, data: selectedFornitore.emittente1 });
      if (selectedFornitore.emittente2?.nome) emittenti.push({ numero: 1, nome: selectedFornitore.emittente2.nome, data: selectedFornitore.emittente2 });
      if (selectedFornitore.emittente3?.nome) emittenti.push({ numero: 2, nome: selectedFornitore.emittente3.nome, data: selectedFornitore.emittente3 });
    }
    return emittenti;
  }, [selectedFornitore]);

  const spotInfo = useMemo(() => {
    if (!selectedFornitore || newRow.emittente_numero === null || newRow.emittente_numero === undefined) return null;
    
    let emittente;
    // Supporta nuovo formato (array emittenti)
    if (selectedFornitore.emittenti && Array.isArray(selectedFornitore.emittenti)) {
      emittente = selectedFornitore.emittenti[newRow.emittente_numero];
    } else {
      // Retrocompatibilità: vecchio formato (emittente1/2/3)
      const oldFormatKey = `emittente${newRow.emittente_numero + 1}`;
      emittente = selectedFornitore[oldFormatKey];
    }
    
    if (!emittente) return null;
    const mapping = { '1min': 'spot_1min', '30sec': 'spot_30sec', '20sec': 'spot_20sec', '15sec': 'spot_15sec' };
    return emittente[mapping[newRow.durata_spot]] || { residuo: 0, prezzo: 0 };
  }, [selectedFornitore, newRow.emittente_numero, newRow.durata_spot]);

  // Funzioni per gestire filtro province
  const handleAddProvinciaFilter = (provincia) => {
    if (!provinceFilter.includes(provincia)) {
      setProvinceFilter([...provinceFilter, provincia].sort());
    }
    setProvinciaSearch('');
    setShowProvinceSelector(false);
  };

  const handleRemoveProvinciaFilter = (provincia) => {
    setProvinceFilter(provinceFilter.filter(p => p !== provincia));
  };

  const clearProvinceFilter = () => {
    setProvinceFilter([]);
  };

  const filteredProvinceOptions = PROVINCE_ITALIANE.filter(p => 
    p.toLowerCase().includes(provinciaSearch.toLowerCase()) &&
    !provinceFilter.includes(p)
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await campagneApi.update(id, {
        titolo: campagna.titolo,
        valore_campagna: campagna.valore_campagna,
        data_inizio: campagna.data_inizio,
        data_fine: campagna.data_fine,
        programmazione: campagna.programmazione,
        note: campagna.note,
        stato: campagna.stato
      });
      toast.success('Campagna salvata');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => setCampagna({ ...campagna, [field]: value });

  const handleSelectFornitore = (fornitore) => {
    setSelectedFornitore(fornitore);
    setNewRow({ ...newRow, fornitore_id: fornitore.id, emittente_numero: 0 });
    setFornitoreSearch(`${fornitore.codice} - ${fornitore.denominazione_sociale}`);
    setShowFornitoreDropdown(false);
  };

  const handleAddRow = async () => {
    if (!newRow.fornitore_id || !newRow.numero_spot) {
      toast.error('Compila tutti i campi');
      return;
    }
    if (spotInfo && newRow.numero_spot > spotInfo.residuo) {
      toast.error(`Spot insufficienti. Disponibili: ${spotInfo.residuo}`);
      return;
    }

    setAddingRow(true);
    try {
      const response = await campagneApi.aggiungiRiga(id, newRow);
      setCampagna(response.data);
      setNewRow({ fornitore_id: '', emittente_numero: 1, durata_spot: '30sec', numero_spot: 1 });
      setSelectedFornitore(null);
      setFornitoreSearch('');
      const fornitoriRes = await fornitoriApi.getAll();
      setFornitori(fornitoriRes.data);
      toast.success('Riga aggiunta - Inventario aggiornato');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta');
    } finally {
      setAddingRow(false);
    }
  };

  const handleRemoveRow = async (rigaId) => {
    try {
      const response = await campagneApi.rimuoviRiga(id, rigaId);
      setCampagna(response.data);
      const fornitoriRes = await fornitoriApi.getAll();
      setFornitori(fornitoriRes.data);
      toast.success('Riga rimossa - Inventario ripristinato');
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  const handleProgrammazioneChange = (newProgrammazione) => {
    handleChange('programmazione', newProgrammazione);
  };

  const formatCurrency = (value) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
  const durataLabels = { '1min': '1 Minuto', '30sec': '30 Secondi', '20sec': '20 Secondi', '15sec': '15 Secondi' };

  if (loading) {
    return (
      <div className="p-6 md:p-10">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!campagna) return null;

  return (
    <div className="p-6 md:p-10" data-testid="costruisci-campagna-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/campagne')} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">{campagna.numero_campagna}</h1>
              <Badge className={campagna.stato === 'confermata' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>{campagna.stato}</Badge>
            </div>
            <div className="text-slate-500 text-sm flex items-center gap-2">
              <Badge variant="outline" className="font-mono">{campagna.cliente_codice}</Badge>
              <span>{campagna.cliente_denominazione}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Select value={campagna.stato} onValueChange={(v) => handleChange('stato', v)}>
            <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bozza">Bozza</SelectItem>
              <SelectItem value="confermata">Confermata</SelectItem>
              <SelectItem value="completata">Completata</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleSave} disabled={saving} data-testid="save-campagna-btn" className="bg-primary hover:bg-primary/90">
            <Save size={18} className="mr-2" />{saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="stats-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-slate-500">Valore Campagna</p><p className="text-xl font-bold font-['Manrope']">{formatCurrency(campagna.valore_campagna)}</p></div>
              <Euro className="h-8 w-8 text-primary/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="stats-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-slate-500">Costo Fornitori</p><p className="text-xl font-bold font-['Manrope']">{formatCurrency(campagna.costo_totale)}</p></div>
              <Radio className="h-8 w-8 text-rose-500/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="stats-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-slate-500">Margine</p><p className={`text-xl font-bold font-['Manrope'] ${campagna.margine >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(campagna.margine)}</p></div>
              <TrendingUp className={`h-8 w-8 ${campagna.margine >= 0 ? 'text-green-500/30' : 'text-red-500/30'}`} />
            </div>
          </CardContent>
        </Card>
        <Card className="stats-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-slate-500">Durata</p><p className="text-xl font-bold font-['Manrope']">{campagna.numero_giorni} giorni</p></div>
              <Calendar className="h-8 w-8 text-amber-500/30" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Campaign Info */}
          <Card>
            <CardHeader><CardTitle className="font-['Manrope']">Dettagli Campagna</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Titolo</Label>
                <Input value={campagna.titolo || ''} onChange={(e) => handleChange('titolo', e.target.value)} placeholder="Es: Promo Estate" data-testid="input-titolo" />
              </div>
              <div className="space-y-2">
                <Label>Valore Campagna (netto IVA)</Label>
                <Input type="number" min="0" step="0.01" value={campagna.valore_campagna} onChange={(e) => handleChange('valore_campagna', parseFloat(e.target.value) || 0)} data-testid="input-valore" />
              </div>
              <div className="space-y-2">
                <Label>Data Inizio</Label>
                <Input type="date" value={campagna.data_inizio} onChange={(e) => handleChange('data_inizio', e.target.value)} data-testid="input-data-inizio" />
              </div>
              <div className="space-y-2">
                <Label>Data Fine</Label>
                <Input type="date" value={campagna.data_fine} onChange={(e) => handleChange('data_fine', e.target.value)} data-testid="input-data-fine" />
              </div>
            </CardContent>
          </Card>

          {/* Add Row Form */}
          <Card data-testid="aggiungi-riga-card">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2"><Plus size={20} className="text-primary" />Aggiungi Emittente</CardTitle>
              <CardDescription>Filtra per copertura provinciale e cerca fornitore</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filtro Copertura Provinciale */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 text-blue-800">
                    <MapPin size={16} />
                    Filtra per Copertura Provinciale
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowProvinceSelector(!showProvinceSelector)}
                    className="border-blue-200 text-blue-700 hover:bg-blue-100"
                    data-testid="add-province-filter-btn"
                  >
                    <Filter size={14} className="mr-1" />
                    Aggiungi Provincia
                  </Button>
                </div>

                {/* Selettore province */}
                {showProvinceSelector && (
                  <div className="p-3 bg-white border rounded-lg space-y-2">
                    <Input
                      placeholder="Cerca provincia (es: BO, MI, RM...)"
                      value={provinciaSearch}
                      onChange={(e) => setProvinciaSearch(e.target.value)}
                      data-testid="search-provincia-filter"
                      className="h-9"
                    />
                    <div className="flex flex-wrap gap-1 max-h-32 overflow-auto">
                      {filteredProvinceOptions.slice(0, 30).map((prov) => (
                        <Button
                          key={prov}
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleAddProvinciaFilter(prov)}
                          data-testid={`filter-provincia-${prov}`}
                        >
                          {prov}
                        </Button>
                      ))}
                      {filteredProvinceOptions.length > 30 && (
                        <span className="text-xs text-slate-500 py-1">...e altre {filteredProvinceOptions.length - 30}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Province selezionate per il filtro */}
                {provinceFilter.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {provinceFilter.map((prov) => (
                        <Badge key={prov} className="bg-blue-600 flex items-center gap-1 px-2 py-1">
                          {prov}
                          <button
                            type="button"
                            onClick={() => handleRemoveProvinciaFilter(prov)}
                            className="ml-1 hover:text-blue-200"
                            data-testid={`remove-filter-${prov}`}
                          >
                            <X size={12} />
                          </button>
                        </Badge>
                      ))}
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={clearProvinceFilter}
                        className="h-6 px-2 text-xs text-blue-600"
                      >
                        Cancella filtro
                      </Button>
                    </div>
                    <p className="text-xs text-blue-700">
                      Mostrando {filteredFornitori.length} fornitori con emittenti che coprono: {provinceFilter.join(', ')}
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-blue-600">Nessun filtro attivo - Mostrando tutti i {fornitori.length} fornitori</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Fornitore (Codice o Denominazione)</Label>
                <div className="relative">
                  <Input
                    value={fornitoreSearch}
                    onChange={(e) => { setFornitoreSearch(e.target.value); setSelectedFornitore(null); setShowFornitoreDropdown(true); }}
                    onFocus={() => setShowFornitoreDropdown(true)}
                    placeholder="Digita codice (FOR-0001) o nome..."
                    data-testid="search-fornitore"
                  />
                  {showFornitoreDropdown && !selectedFornitore && filteredFornitori.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-auto">
                      {filteredFornitori.map((f) => {
                        const matchingEmittenti = getEmittentiMatchingFilter(f);
                        return (
                          <button
                            key={f.id}
                            className="w-full px-4 py-3 text-left hover:bg-slate-50 text-sm border-b last:border-b-0"
                            onClick={() => handleSelectFornitore(f)}
                            data-testid={`fornitore-option-${f.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="font-mono text-xs">{f.codice}</Badge>
                              <span className="font-medium">{f.denominazione_sociale}</span>
                            </div>
                            {matchingEmittenti && matchingEmittenti.length > 0 && (
                              <div className="mt-1 text-xs text-green-600 flex items-center gap-1">
                                <MapPin size={10} />
                                Emittenti con copertura: {matchingEmittenti.join(', ')}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {showFornitoreDropdown && !selectedFornitore && filteredFornitori.length === 0 && fornitoreSearch && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg p-4 text-center text-slate-500 text-sm">
                      Nessun fornitore trovato
                      {provinceFilter.length > 0 && (
                        <p className="text-xs mt-1">Prova a rimuovere il filtro provinciale</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {selectedFornitore && (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Emittente</Label>
                      <Select value={String(newRow.emittente_numero)} onValueChange={(v) => setNewRow({ ...newRow, emittente_numero: parseInt(v) })}>
                        <SelectTrigger data-testid="select-emittente"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {emittentiDisponibili.map((em) => (<SelectItem key={em.numero} value={String(em.numero)}>{em.nome}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Durata Spot</Label>
                      <Select value={newRow.durata_spot} onValueChange={(v) => setNewRow({ ...newRow, durata_spot: v })}>
                        <SelectTrigger data-testid="select-durata"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1min">1 Minuto</SelectItem>
                          <SelectItem value="30sec">30 Secondi</SelectItem>
                          <SelectItem value="20sec">20 Secondi</SelectItem>
                          <SelectItem value="15sec">15 Secondi</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>N° Spot</Label>
                      <Input type="number" min="1" value={newRow.numero_spot} onChange={(e) => setNewRow({ ...newRow, numero_spot: parseInt(e.target.value) || 1 })} data-testid="input-numero-spot" />
                    </div>
                  </div>

                  {spotInfo && (
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div><p className="text-slate-500">Disponibili</p><p className="font-semibold">{spotInfo.residuo} spot</p></div>
                        <div><p className="text-slate-500">Prezzo unitario</p><p className="font-semibold">{formatCurrency(spotInfo.prezzo)}</p></div>
                        <div><p className="text-slate-500">Costo totale</p><p className="font-semibold text-primary">{formatCurrency(spotInfo.prezzo * newRow.numero_spot)}</p></div>
                      </div>
                      {newRow.numero_spot > spotInfo.residuo && (
                        <div className="mt-2 flex items-center gap-2 text-red-600 text-sm"><AlertCircle size={16} />Spot insufficienti!</div>
                      )}
                    </div>
                  )}

                  <Button onClick={handleAddRow} disabled={addingRow || (spotInfo && newRow.numero_spot > spotInfo.residuo)} className="w-full" data-testid="btn-aggiungi-riga">
                    <Plus size={18} className="mr-2" />{addingRow ? 'Aggiunta in corso...' : 'Aggiungi Emittente alla Campagna'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Rows Table */}
          <Card data-testid="righe-campagna-card">
            <CardHeader><CardTitle className="font-['Manrope']">Emittenti nella Campagna ({campagna.righe?.length || 0})</CardTitle></CardHeader>
            <CardContent>
              {!campagna.righe?.length ? (
                <div className="text-center py-8 text-slate-500">
                  <Radio size={40} className="mx-auto mb-3 text-slate-300" />
                  <p>Nessuna emittente aggiunta</p>
                  <p className="text-sm">Usa il form sopra per aggiungere emittenti alla campagna</p>
                </div>
              ) : (
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Codice</TableHead>
                        <TableHead>Fornitore</TableHead>
                        <TableHead>Emittente</TableHead>
                        <TableHead>Durata</TableHead>
                        <TableHead className="text-right">N° Spot</TableHead>
                        <TableHead className="text-right">Prezzo Unit.</TableHead>
                        <TableHead className="text-right">Totale</TableHead>
                        <TableHead className="w-[60px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {campagna.righe.map((riga) => (
                        <TableRow key={riga.id}>
                          <TableCell><Badge variant="outline" className="font-mono text-xs">{riga.fornitore_codice}</Badge></TableCell>
                          <TableCell className="font-medium">{riga.fornitore_denominazione}</TableCell>
                          <TableCell>{riga.emittente_nome}</TableCell>
                          <TableCell>{durataLabels[riga.durata_spot]}</TableCell>
                          <TableCell className="text-right">{riga.numero_spot}</TableCell>
                          <TableCell className="text-right">{formatCurrency(riga.prezzo_unitario)}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(riga.costo_totale)}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleRemoveRow(riga.id)} data-testid={`remove-riga-${riga.id}`}>
                              <Trash2 size={16} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <CalendarioProgrammazione
            dataInizio={campagna.data_inizio}
            dataFine={campagna.data_fine}
            programmazione={campagna.programmazione || []}
            onProgrammazioneChange={handleProgrammazioneChange}
          />

          <Card>
            <CardHeader><CardTitle className="font-['Manrope']">Note</CardTitle></CardHeader>
            <CardContent>
              <Textarea value={campagna.note || ''} onChange={(e) => handleChange('note', e.target.value)} placeholder="Note sulla campagna..." rows={4} data-testid="input-note" />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CostruisciCampagnaPage;
